import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LargeContentBox } from "@/components/common/LargeContentBox";
import { ScreenMobile } from "@/components/common/ScreenMobile";
import { Add, HealthAndSafety } from "@mui/icons-material";
import {
  Box,
  Stack,
  Typography,
  Button,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { toast } from "react-toastify";
import { useSessionContext } from "@/contexts/SessionContext";
import { Coverage } from "fhir/r5";
import { CoverageModal } from "./components/CoverageModal";
import { InsuranceCardsView } from "./components/InsuranceCardsView";
import { useCoverage } from "@/services/fhir/coverage/useCoverage";
import { oids } from "@/configs/Settings";
import { PageHeader } from "@/components/common/PageHeader";
import { PatchCoverage } from "@/services/fhir/coverage/PatchCoverage";

export default function PatientInsurance() {
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const { user } = useSessionContext();
  const patientId = `${oids.cpf}-${user.username}`;

  const [screenOpen, setScreenOpen] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCoverage, setSelectedCoverage] = useState<Coverage | null>(
    null
  );

  const { coverage, loading, fetchCoverage } = useCoverage();

  useEffect(() => {
    if (patientId) {
      fetchCoverage({ patient: patientId });
    }
  }, [patientId, fetchCoverage]);

  const handleCloseScreen = () => {
    setScreenOpen(false);
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/home");
    }
  };

  const handleOpenNew = () => {
    setSelectedCoverage(null);
    setIsModalOpen(true);
  };

  const handleEdit = (item: Coverage) => {
    setSelectedCoverage(item);
    setIsModalOpen(true);
  };

  const handleToggleStatus = async (item: Coverage) => {
    try {
      await PatchCoverage({
        id: item.id || "",
        status: item.status === "active" ? "cancelled" : "active",
        hasProperty: item.hasOwnProperty("status"),
      });
      toast.success("Atualizado com sucesso!");
      fetchCoverage({ patient: patientId });
    } catch {
      toast.warning(
        "Não foi possível finalizar a ação, por favor tente novamente."
      );
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCoverage(null);
  };

  const handleSuccess = () => {
    fetchCoverage({ patient: patientId });
  };

  const addButton = (
    <Button
      variant="contained"
      color="primary"
      startIcon={<Add />}
      onClick={handleOpenNew}
      fullWidth={isMobileLayout}
      sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2.5 }}
    >
      Nova Carteirinha
    </Button>
  );

  const bodyContent = (
    <>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 6,
          }}
        >
          <CircularProgress size={40} color="primary" sx={{ mb: 2 }} />
          <Typography variant="body1" color="neutral700.main">
            Carregando carteirinhas...
          </Typography>
        </Box>
      ) : coverage.length > 0 ? (
        <InsuranceCardsView
          coverages={coverage}
          onEdit={handleEdit}
          onToggleStatus={handleToggleStatus}
        />
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
            px: 2,
            borderRadius: 3,
            bgcolor: "neutral100.main",
            border: "1px dashed",
            borderColor: "divider",
          }}
        >
          <HealthAndSafety
            sx={{
              fontSize: 56,
              color: "primary.main",
              mb: 2,
              opacity: 0.65,
            }}
          />
          <Typography variant="h6" color="neutral700.main" gutterBottom>
            Nenhuma carteirinha cadastrada
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ maxWidth: 360, mb: 3 }}
          >
            Adicione sua carteirinha do plano de saúde para consultar e usar nos
            agendamentos.
          </Typography>
          {addButton}
        </Box>
      )}
    </>
  );

  const coverageModal = isModalOpen ? (
    <CoverageModal
      open={isModalOpen}
      isEditing={!!selectedCoverage}
      patientId={user.username || ""}
      initialData={
        selectedCoverage
          ? {
              organization: {
                value:
                  selectedCoverage.insurer?.reference?.split("/")[1] || "",
                label: selectedCoverage.insurer?.display || "",
              },
              insurancePlan: {
                value:
                  selectedCoverage.insurancePlan?.reference?.split("/")[1] ||
                  "",
                label: selectedCoverage.insurancePlan?.display || "",
                display: selectedCoverage.insurancePlan?.display,
              },
              cardNumber: selectedCoverage.identifier?.[0]?.value || "",
              id: selectedCoverage.id,
            }
          : undefined
      }
      onClose={handleCloseModal}
      onSuccess={handleSuccess}
    />
  ) : null;

  if (isMobileLayout) {
    return (
      <>
        <ScreenMobile
          title="Planos de saúde"
          open={screenOpen}
          onClose={handleCloseScreen}
        >
          <Stack
            spacing={1.5}
            sx={{
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              px: 1.5,
              pb: 2,
              pt: 0.5,
              boxSizing: "border-box",
            }}
          >
            <Typography variant="body2" color="text.secondary" lineHeight={1.4}>
              Gerencie seus cartões de saúde e convênios.
            </Typography>
            {addButton}
            {bodyContent}
          </Stack>
        </ScreenMobile>
        {coverageModal}
      </>
    );
  }

  return (
    <LargeContentBox>
      <Stack sx={{ width: "100%" }} spacing={2.5}>
        <PageHeader
          title="Planos de Saúde"
          icon={HealthAndSafety}
          description="Gerencie seus planos de saúde"
          action={addButton}
        />
        {bodyContent}
      </Stack>
      {coverageModal}
    </LargeContentBox>
  );
}
